<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

interface ProcessStep {
  title: string;
  description: string;
}

type FlThemeVars = Record<string, string>;

const themes: Record<"flutter" | "vaah", { dark: FlThemeVars; light: FlThemeVars }> = {
  flutter: {
    dark: {
      "--fl-eyebrow": "#887fff",
      "--fl-dot": "#ba55fe",
      "--fl-dot-glow": "rgba(186, 85, 254, 0.8)",
      "--fl-grad-a": "#12dbff",
      "--fl-grad-b": "#ba55fe",
      "--fl-line": "linear-gradient(180deg, rgba(18, 219, 255, 0.65), rgba(186, 85, 254, 0.65) 55%, rgba(186, 85, 254, 0.15))",
      "--fl-line-glow": "0 0 12px rgba(186, 85, 254, 0.35)",
      "--fl-connector-a": "rgba(18, 219, 255, 0.55)",
      "--fl-connector-b": "rgba(136, 127, 255, 0.25)",
      "--fl-node": "linear-gradient(135deg, #12dbff, #ba55fe)",
      "--fl-node-glow": "0 0 10px rgba(18, 219, 255, 0.6)",
      "--fl-node-pulse": "rgba(186, 85, 254, 0.45)",
      "--fl-card-bg": "rgba(18, 0, 44, 0.35)",
      "--fl-card-border": "rgba(136, 127, 255, 0.15)",
      "--fl-card-shadow": "none",
      "--fl-card-hover-border": "rgba(18, 219, 255, 0.4)",
      "--fl-card-hover-shadow": "0 18px 36px -22px rgba(18, 219, 255, 0.4)",
      "--fl-title": "#fff",
      "--fl-desc": "#bcb2c3",
    },
    light: {
      "--fl-eyebrow": "#613faa",
      "--fl-dot": "#ba55fe",
      "--fl-dot-glow": "rgba(186, 85, 254, 0.8)",
      "--fl-grad-a": "#12dbff",
      "--fl-grad-b": "#ba55fe",
      "--fl-line": "linear-gradient(180deg, rgba(18, 219, 255, 0.65), rgba(186, 85, 254, 0.65) 55%, rgba(186, 85, 254, 0.15))",
      "--fl-line-glow": "0 0 12px rgba(186, 85, 254, 0.35)",
      "--fl-connector-a": "rgba(18, 219, 255, 0.55)",
      "--fl-connector-b": "rgba(136, 127, 255, 0.25)",
      "--fl-node": "linear-gradient(135deg, #12dbff, #ba55fe)",
      "--fl-node-glow": "0 0 10px rgba(18, 219, 255, 0.5)",
      "--fl-node-pulse": "rgba(186, 85, 254, 0.45)",
      "--fl-card-bg": "#fff",
      "--fl-card-border": "rgba(208, 208, 208, 0.5)",
      "--fl-card-shadow": "0 2px 8px rgba(0, 0, 0, 0.04)",
      "--fl-card-hover-border": "rgba(18, 219, 255, 0.5)",
      "--fl-card-hover-shadow": "0 8px 24px rgba(18, 219, 255, 0.18)",
      "--fl-title": "#613faa",
      "--fl-desc": "#333",
    },
  },
  vaah: {
    dark: {
      "--fl-eyebrow": "#8B7BFF",
      "--fl-dot": "#4753DC",
      "--fl-dot-glow": "rgba(71, 83, 220, 0.8)",
      "--fl-grad-a": "#4FA3FF",
      "--fl-grad-b": "#8B7BFF",
      "--fl-line": "linear-gradient(180deg, rgba(79, 163, 255, 0.65), rgba(139, 123, 255, 0.65) 55%, rgba(139, 123, 255, 0.15))",
      "--fl-line-glow": "0 0 12px rgba(71, 83, 220, 0.35)",
      "--fl-connector-a": "rgba(79, 163, 255, 0.55)",
      "--fl-connector-b": "rgba(139, 123, 255, 0.25)",
      "--fl-node": "linear-gradient(135deg, #4FA3FF, #4753DC)",
      "--fl-node-glow": "0 0 10px rgba(71, 83, 220, 0.6)",
      "--fl-node-pulse": "rgba(139, 123, 255, 0.45)",
      "--fl-card-bg": "rgba(33, 39, 108, 0.35)",
      "--fl-card-border": "rgba(139, 123, 255, 0.2)",
      "--fl-card-shadow": "none",
      "--fl-card-hover-border": "rgba(79, 163, 255, 0.4)",
      "--fl-card-hover-shadow": "0 18px 36px -22px rgba(71, 83, 220, 0.4)",
      "--fl-title": "#fff",
      "--fl-desc": "#7B84E8",
    },
    light: {
      "--fl-eyebrow": "#6d2d9e",
      "--fl-dot": "#6d2d9e",
      "--fl-dot-glow": "rgba(109, 45, 158, 0.8)",
      "--fl-grad-a": "#4FA3FF",
      "--fl-grad-b": "#8B7BFF",
      "--fl-line": "linear-gradient(180deg, rgba(109, 45, 158, 0.65), rgba(148, 103, 192, 0.65) 55%, rgba(148, 103, 192, 0.15))",
      "--fl-line-glow": "0 0 12px rgba(109, 45, 158, 0.35)",
      "--fl-connector-a": "rgba(109, 45, 158, 0.55)",
      "--fl-connector-b": "rgba(148, 103, 192, 0.25)",
      "--fl-node": "linear-gradient(135deg, #8B7BFF, #6d2d9e)",
      "--fl-node-glow": "0 0 10px rgba(109, 45, 158, 0.5)",
      "--fl-node-pulse": "rgba(148, 103, 192, 0.45)",
      "--fl-card-bg": "#fff",
      "--fl-card-border": "rgba(208, 208, 208, 0.5)",
      "--fl-card-shadow": "0 2px 8px rgba(0, 0, 0, 0.04)",
      "--fl-card-hover-border": "rgba(109, 45, 158, 0.5)",
      "--fl-card-hover-shadow": "0 8px 24px rgba(109, 45, 158, 0.18)",
      "--fl-title": "#6d2d9e",
      "--fl-desc": "#5A5478",
    },
  },
};

const emit = defineEmits<{ "cta-clicked": [] }>();

const props = withDefaults(
  defineProps<{
    heading: string;
    subcopy: string;
    steps: ProcessStep[];
    eyebrow?: string;
    ctaText?: string;
    ctaLink?: string | null;
    variant?: "flutter" | "vaah";
  }>(),
  {
    eyebrow: "Our process",
    ctaText: "See our process",
    ctaLink: null,
    variant: "flutter",
  },
);

const themeVars = computed<FlThemeVars>(() =>
  themes[props.variant][isLight.value ? "light" : "dark"]
);
</script>

<template>
  <section class="relative" :style="themeVars">
    <div class="text-center">
      <span v-if="eyebrow" class="inline-flex items-center gap-2 text-[11px] lg:text-[12px] font-semibold uppercase tracking-[0.2em] mb-[14px] text-[var(--fl-eyebrow)]">
        <span class="fl-eyebrow-dot w-[6px] h-[6px] rounded-full" />{{ eyebrow }}
      </span>
      <h2 class="fl-heading font-semibold text-[clamp(28px,4vw,48px)] leading-[1.1] text-center">
        {{ heading }}
      </h2>
      <p 
        class="font-avant-garde text-[16px] lg:text-[18px] leading-[1.6] tracking-[0.36px] mt-4 max-w-[720px] mx-auto px-4 text-center"
        :class="isLight ? 'text-black opacity-70' : 'text-white opacity-70'"
      >
        {{ subcopy }}
      </p>
    </div>

    <div class="fl-timeline relative max-w-[800px] mx-auto px-4 mt-12 lg:mt-16">
      <div class="fl-tl-line absolute top-2 bottom-2 left-[38px] lg:left-1/2 w-[2px] lg:-translate-x-1/2" />
      <div 
        v-for="(step, i) in steps" 
        :key="step.title" 
        class="fl-tl-item relative pb-8 lg:pb-0 pl-[52px] lg:w-1/2 last:pb-0"
        :class="[
          i % 2 === 1 ? 'fl-tl-right lg:ml-[50%] lg:pl-[44px] lg:pr-0' : 'lg:pl-0 lg:pr-[44px]',
          i > 0 ? 'lg:-mt-[60px]' : '',
        ]"
      >
        <div class="fl-tl-node absolute top-[2px] left-[16px] lg:left-auto lg:right-[-7px] z-20 w-[14px] h-[14px] rounded-full" />
        <div class="fl-tl-card rounded-[14px] border border-[var(--fl-card-border)] bg-[var(--fl-card-bg)] p-[16px_18px] shadow-[var(--fl-card-shadow)] transition-[transform,border-color,box-shadow] duration-300 ease-in-out hover:-translate-y-1 hover:border-[var(--fl-card-hover-border)] hover:shadow-[var(--fl-card-hover-shadow)]">
          <span class="fl-tl-num block mb-2 md:mb-[10px] text-[22px] font-semibold leading-none">
            {{ String(i + 1).padStart(2, "0") }}
          </span>
          <h3 class="text-[17px] font-semibold text-[var(--fl-title)]">
            {{ step.title }}
          </h3>
          <p class="text-[13px] lg:text-[14px] leading-[1.6] text-[var(--fl-desc)] mt-1">
            {{ step.description }}
          </p>
        </div>
      </div>
    </div>

    <div class="flex justify-center mt-10 lg:mt-12">
      <WrButton
        :link="ctaLink || undefined"
        @click="!ctaLink ? emit('cta-clicked') : undefined"
        container-class="h-[44px] lg:h-[48px]"
        append-class="text-sm lg:font-normal px-5 lg:px-6"
      >
        {{ ctaText }}
      </WrButton>
    </div>
  </section>
</template>

<style scoped>
/* Keyframe Animations & Custom Glows */
.fl-eyebrow-dot {
  background: var(--fl-dot);
  box-shadow: 0 0 8px var(--fl-dot-glow);
  animation: fl-blink 2.4s ease-in-out infinite;
}
@keyframes fl-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }

/* Font definitions & Complex Text Gradients */
.fl-heading, .fl-tl-num {
  font-family: "ITC Avant Garde Gothic Demi", "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  background: linear-gradient(92deg, var(--fl-grad-a) -3.57%, var(--fl-grad-b) 80.72%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.font-avant-garde {
  font-family: "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
}

/* Dynamic CSS Variable Line and Node Styles */
.fl-tl-line {
  background: var(--fl-line);
  box-shadow: var(--fl-line-glow);
}

.fl-tl-item::before {
  content: "";
  position: absolute;
  left: 30px;
  top: 8px;
  width: 22px;
  height: 2px;
  background: linear-gradient(90deg, var(--fl-connector-a), var(--fl-connector-b));
}

.fl-tl-node {
  background: var(--fl-node);
  box-shadow: var(--fl-node-glow);
}

.fl-tl-node::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50%;
  animation: fl-node-pulse 2.4s ease-out infinite;
}

@keyframes fl-node-pulse {
  0% { box-shadow: 0 0 0 0 var(--fl-node-pulse); }
  70% { box-shadow: 0 0 0 10px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
}

/* Timeline Layout Responsive Connectors & Alternate Node Placement */
@media (min-width: 1024px) {
  .fl-tl-item::before {
    left: auto;
    right: 7px;
    width: 37px;
    background: linear-gradient(90deg, var(--fl-connector-b), var(--fl-connector-a));
  }
  .fl-tl-right::before {
    right: auto;
    left: 7px;
    background: linear-gradient(90deg, var(--fl-connector-a), var(--fl-connector-b));
  }
  .fl-tl-right .fl-tl-node {
    right: auto;
    left: -7px;
  }
}
</style>